import type { Reference, ServiceRequest } from '@medplum/fhirtypes';
import { REQUESTED_GROUP_TEMPLATE_EXT } from '@unify/fhir-model/constants';

function normalizeHealthcareServiceRef(ref: string | undefined): string | undefined {
  const trimmed = ref?.trim();
  if (!trimmed) return undefined;
  return trimmed.startsWith('HealthcareService/')
    ? trimmed
    : `HealthcareService/${trimmed.replace(/^HealthcareService\//, '')}`;
}

/**
 * Canonical waitlist key: Group Template HealthcareService reference on
 * `ServiceRequest.extension[requested-group-template].valueReference`.
 */
export function getRequestedGroupTemplateRef(
  resource: ServiceRequest,
): Reference | undefined {
  return resource.extension?.find((e) => e.url === REQUESTED_GROUP_TEMPLATE_EXT)
    ?.valueReference;
}

/** Set or clear the Group Template the client is waiting to join. */
export function setRequestedGroupTemplateRef(
  resource: ServiceRequest,
  ref: Reference | string | undefined,
): void {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== REQUESTED_GROUP_TEMPLATE_EXT,
  );
  if (!ref) {
    resource.extension = others.length ? others : undefined;
    return;
  }
  const valueReference: Reference =
    typeof ref === 'string'
      ? { reference: normalizeHealthcareServiceRef(ref) }
      : {
          ...ref,
          reference: normalizeHealthcareServiceRef(ref.reference) ?? ref.reference,
        };
  resource.extension = [
    ...others,
    { url: REQUESTED_GROUP_TEMPLATE_EXT, valueReference },
  ];
}

/** True when SR waitlist key matches the Group's template HealthcareService ref. */
export function requestedTemplateMatchesGroupTemplate(
  sr: ServiceRequest,
  groupTemplateReference: string | undefined,
): boolean {
  const wanted = normalizeHealthcareServiceRef(groupTemplateReference);
  if (!wanted) return false;
  const requested = normalizeHealthcareServiceRef(getRequestedGroupTemplateRef(sr)?.reference);
  return requested === wanted;
}
